/**
 * AdminLobbyView — the host's lobby screen.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────┐
 *   │ QUALITY DOMINATION (wordmark)               │
 *   │                                             │
 *   │   ┌───────────┐    WAITING FOR PLAYERS…     │
 *   │   │           │    1. ALI                   │
 *   │   │   QR      │    2. — — —                 │
 *   │   │           │    3. — — —                 │
 *   │   └───────────┘    …                        │
 *   │                                             │
 *   │ GAME RULES ▸                  BEGIN MATCH ▸ │
 *   └─────────────────────────────────────────────┘
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { ChalkText } from '../base/ChalkText';
import { ChalkButton } from '../base/ChalkButton';
import { ChalkQR } from './ChalkQR';
import { RosterList } from './RosterList';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  LOBBY_MAX_PLAYERS,
  useLobbyStore,
  usePlayers,
} from '../../stores/lobbyStore';

interface AdminLobbyViewProps {
  sessionId: string;
}

export function AdminLobbyView({ sessionId }: AdminLobbyViewProps) {
  const players = usePlayers();
  const beginMatch = useLobbyStore((s) => s.beginMatch);
  const disconnect = useLobbyStore((s) => s.disconnect);
  const wsState = useLobbyStore((s) => s.wsState);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Keep the QR ≥ 200px so phone-camera scans across a classroom remain
  // reliable; below ~180px the chalkRoughen filter degrades the
  // module-edge contrast enough to fail under fluorescent lighting.
  const qrSize = isMobile ? 200 : 280;

  const handleQuit = () => {
    disconnect();
    navigate('/');
  };

  // Server enforces "≥2 ready and connected"; we mirror the rule so the
  // button is disabled until the lobby legitimately can begin. We also
  // disable while the WebSocket is mid-handshake or backing off — sending
  // beginMatch on a closed socket silently drops the message and the user
  // ends up clicking repeatedly with no feedback. The actual navigation
  // to /play is driven by the `phase==='playing'` watcher in <Lobby/>;
  // clicking BEGIN just dispatches to the server.
  const readyCount = useMemo(
    () => players.filter((p) => p.isReady && p.isConnected).length,
    [players],
  );
  const isConnected = wsState === 'open';
  const canBegin = readyCount >= 2 && isConnected;

  const joinUrl = useMemo(() => {
    if (typeof window === 'undefined') return `/join/${sessionId}`;
    return `${window.location.origin}/join/${sessionId}`;
  }, [sessionId]);

  return (
    <div
      style={{
        display: 'grid',
        gap: isMobile ? 16 : 32,
        gridTemplateRows: 'auto 1fr auto',
        minHeight: 'calc(100dvh - 96px)',
      }}
    >
      <header>
        <ChalkText size="display" underline>
          Quality Domination
        </ChalkText>
        <p
          style={{
            marginTop: 8,
            fontFamily: fontStacks.body,
            fontSize: isMobile ? 14 : 16,
            color: surfaces.chalk,
            opacity: 0.62,
          }}
        >
          Share the QR with players, or read out the session code at the
          bottom of the chalkboard.
        </p>
      </header>

      <section
        style={{
          display: 'grid',
          gap: isMobile ? 24 : 56,
          // Single-column stack on mobile — QR sits above the roster so
          // it's the first thing visible above the fold.
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          alignItems: 'center',
          gridAutoFlow: 'row',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {/* Washi-tape pinned QR rotated -2deg per the spec. */}
          <div style={{ position: 'relative', transform: 'rotate(-2deg)' }}>
            <ChalkQR value={joinUrl} size={qrSize} />
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: -14,
                left: -22,
                width: 88,
                height: 24,
                background: '#9C9282',
                opacity: 0.7,
                transform: 'rotate(-12deg)',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
              }}
            />
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: -14,
                right: -22,
                width: 88,
                height: 24,
                background: '#9C9282',
                opacity: 0.7,
                transform: 'rotate(14deg)',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            width: '100%',
            maxWidth: 400,
            // When stacked on mobile, centre the roster column under the QR.
            margin: isMobile ? '0 auto' : undefined,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontFamily: fontStacks.display, fontSize: isMobile ? 24 : 32, color: surfaces.paper }}>Waiting for Players</h2>
            <span style={{ fontFamily: fontStacks.body, color: surfaces.paper, opacity: 0.8, fontSize: isMobile ? 14 : 16, fontWeight: 'bold' }}>{players.length} / {LOBBY_MAX_PLAYERS} (Min 2)</span>
          </div>
          <RosterList players={players} maxPlayers={LOBBY_MAX_PLAYERS} />

          <div style={{ marginTop: isMobile ? 12 : 24, padding: isMobile ? 14 : 20, background: 'rgba(255,255,255,0.15)', borderRadius: 16, border: '2px solid rgba(255,255,255,0.3)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontFamily: fontStacks.display, color: surfaces.paper, fontSize: isMobile ? 16 : 18, textTransform: 'uppercase' }}>Quick Guide</h3>
            <p style={{ margin: 0, fontFamily: fontStacks.body, color: surfaces.paper, opacity: 0.9, fontSize: isMobile ? 13 : 14, lineHeight: 1.5 }}>Conquer territories by answering ISO 29119-3 questions. Lead Tester starts the match when two or more players are ready.</p>
          </div>
        </div>
      </section>

      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 12 : 24,
          flexWrap: 'wrap',
          marginTop: isMobile ? 20 : 40,
        }}
      >
        <ChalkButton
          variant="secondary"
          size="lg"
          onClick={handleQuit}
          style={{
            padding: isMobile ? '16px 24px' : '24px 36px',
            fontSize: isMobile ? 18 : 24,
          }}
        >
          Quit to Main Page
        </ChalkButton>
        <ChalkButton
          variant="primary"
          size="lg"
          disabled={!canBegin}
          onClick={() => beginMatch()}
          style={{
            padding: isMobile ? '16px 24px' : '24px 48px',
            fontSize: isMobile ? 20 : 28,
          }}
        >
          Begin Match
        </ChalkButton>
      </footer>
    </div>
  );
}


